export type SignInRecord = {
  id: string;
  userId: string | null;
  workerName: string;
  jobTitle?: string | null;
  siteId?: string;
  siteName?: string;
  signInTime: string | number | Date;
  signOutTime?: string | number | Date | null;
  method: string;
  flagged?: boolean;
  distanceFromSite?: number | null;
  withinGeofence?: boolean | null;
  timezone?: string | null;
};

export type SignInGroup = {
  ts: number; // start-of-day timestamp
  label: string; // "Today" or locale date string
  items: SignInRecord[];
};

export function startOfDayTs(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

export function isToday(d: Date): boolean {
  const now = new Date();
  return startOfDayTs(d) === startOfDayTs(now);
}

export function formatGroupLabel(d: Date, locale?: string | string[]): string {
  return isToday(d) ? "Today" : d.toLocaleDateString(locale);
}

export function formatSignInTime(d: Date, locale?: string | string[]): string {
  return d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
}

export function isSameDay(a: Date, b: Date): boolean {
  return startOfDayTs(a) === startOfDayTs(b);
}

export function formatSignOutTime(
  signOut: Date,
  signIn: Date,
  locale?: string | string[]
): string {
  const time = signOut.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
  });
  if (isSameDay(signIn, signOut)) return time;
  return `${time} (${signOut.toLocaleDateString(locale, { month: "short", day: "numeric" })})`;
}

export function formatDuration(
  from: Date | string | number,
  to?: Date | string | number | null
): string {
  const start = toDate(from);
  const end = to == null ? new Date() : toDate(to);
  const ms = Math.max(0, end.getTime() - start.getTime());
  const mins = Math.floor(ms / 60000);
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  if (hours > 0) return `${hours}h ${remMins}m`;
  return `${remMins}m`;
}

export function groupSignInsByDate(
  records: SignInRecord[],
  locale?: string | string[]
): SignInGroup[] {
  const groups = new Map<number, SignInGroup>();

  for (const r of records) {
    const date = toDate(r.signInTime);
    const ts = startOfDayTs(date);
    let group = groups.get(ts);
    if (!group) {
      group = { ts, label: formatGroupLabel(date, locale), items: [] };
      groups.set(ts, group);
    }
    group.items.push(r);
  }

  const ordered = Array.from(groups.values()).sort((a, b) => b.ts - a.ts);
  for (const g of ordered) {
    g.items.sort(
      (a, b) => toDate(b.signInTime).getTime() - toDate(a.signInTime).getTime()
    );
  }
  return ordered;
}

function toDate(value: Date | string | number): Date {
  return value instanceof Date ? value : new Date(value);
}
